/** Service Desk console: Settings -> Connectors (SD-SC-17), part D: import wizard and inbound hooks. */
export const serviceConnectorsD = {
  import: {
    title: "Import an API",
    subtitle: "Create a connector and its operations from an OpenAPI or Swagger file, a Postman collection or a curl command.",
    subtitleExisting: "Add operations to {{name}} from an OpenAPI or Swagger file, a Postman collection or a curl command.",
    stepsLabel: "Import steps",
    stepDone: "(completed)",
    stepOf: "Step {{step}} of {{count}} · {{name}}",
    steps: { source: "Source", operations: "Operations found", auth: "Auth and environments", review: "Review and create" },
    sources: {
      openapi: { title: "OpenAPI / Swagger", hint: "JSON or YAML file, or a public HTTPS URL." },
      postman: { title: "Postman collection", hint: "Collection v2.1 export." },
      curl: { title: "A curl command", hint: "One request becomes one operation." },
    },
    whereDocs: "Where are the API docs?",
    how: "How to provide it",
    paste: "Paste or upload",
    fromUrl: "From a URL",
    contentLabel: "Document",
    contentHint: "Paste the JSON or YAML, or upload a file up to {{mb}} MB. External references aren't followed.",
    contentRequired: "Paste the document or upload a file.",
    upload: "Upload a file",
    fileRead: "{{name}} read",
    tooLarge: "The file is too large. Specs are limited to {{mb}} MB: split it or remove examples.",
    urlLabel: "Spec URL",
    urlHint: "A public HTTPS address. Fetched through the same guard as every call.",
    curlLabel: "curl command",
    curlHint: "Headers that carry credentials are removed; you add the secret in step 3.",
    curlRequired: "Paste one curl command with an https:// URL.",
    read: "Read the document",
    found_one: "{{count}} operation found",
    found_other: "{{count}} operations found",
    stripped_one: "{{count}} example value looked like a secret and was removed. Add credentials in the next step; they are sealed, never stored in operations.",
    stripped_other: "{{count}} example values looked like secrets and were removed. Add credentials in the next step; they are sealed, never stored in operations.",
    truncated: "More operations were found than are kept. Import the rest from a smaller file.",
    warnings_one: "{{count}} thing to look at",
    warnings_other: "{{count}} things to look at",
    warning: {
      document_truncated: "The document was cut because it is very large.",
      operations_truncated: "Only the first operations were kept.",
      params_truncated: "Some parameters were left out.",
      server_url_refused: "A server address was refused (not public HTTPS).",
      no_server_url: "The document names no server address: enter the base URL in step 3.",
      path_refused: "A path was refused (dot segments or a second host).",
      method_unsupported: "A method Sagot does not call was skipped.",
      auth_scheme_unsupported: "An authentication scheme could not be mapped: set it by hand.",
      cookie_param_ignored: "A cookie parameter was ignored.",
      header_param_ignored: "A header parameter was ignored (credentials come from the sealed authentication).",
      secret_stripped: "A secret-looking example value was removed.",
      template_syntax_removed: "Template-like text in the document was neutralised.",
      pattern_dropped: "A value pattern was dropped.",
      schema_simplified: "A complex schema was simplified.",
      recursive_schema: "A self-referencing schema was cut.",
      ref_unresolved: "A reference could not be resolved.",
      instruction_like_text: "Text that reads like an instruction was removed from a description.",
      file_upload_ignored: "A file upload field was ignored.",
      file_reference_ignored: "A file reference was ignored.",
      body_unsupported: "A body type Sagot does not send was skipped.",
    },
    tableLabel: "Operations found",
    selectAll: "Select all operations",
    pick: "Import {{key}}",
    keyTaken: "Key in use: it will be renamed",
    writeNote: "Operations that change data come in as drafts nobody may call yet. You choose who may invoke each one, then publish it.",
    noneTitle: "Nothing to import",
    noneBody: "No operations were found in this document. Check the file and try again.",
    back: "Back",
    next: "Next",
    productionUrl: "Production base URL",
    sandboxUrl: "Sandbox base URL (optional)",
    sandboxHint: "If you give one, calls start in the sandbox.",
    schemes: "Authentication schemes in the document",
    schemesHint: "Pick one to fill in the authentication below.",
    pickScheme: "Choose a scheme",
    secretsTitle: "Secrets",
    secretsNote: "Typed here, sealed in the vault when the connector is created and never shown again. You can also set them later.",
    secretFor: "Value for {{slot}}",
    environments: "Environments",
    secretsSealed_one: "{{count}} secret will be sealed",
    secretsSealed_other: "{{count}} secrets will be sealed",
    secretsLater: "None yet: set them on the connector",
    selected_one: "{{count}} operation selected",
    selected_other: "{{count}} operations selected",
    addTo: "Add to",
    reviewNote: "Created as a draft until you test one operation. Every operation gets a description for the assistant that you should review.",
    reviewExisting: "The operations are added as drafts. Review each one, test it, then publish.",
    create: "Create connector",
    addOperations_one: "Add {{count}} operation",
    addOperations_other: "Add {{count}} operations",
    created_one: "{{name}} created with {{count}} operation",
    created_other: "{{name}} created with {{count}} operations",
    added_one: "{{count}} operation added",
    added_other: "{{count}} operations added",
  },
  hooks: {
    intro: "A customer system calls a Sagot address when something happens (an order shipped, a record changed). Every call is verified, deduplicated and turned into an event that can start a workflow.",
    add: "Add inbound endpoint",
    edit: "Edit {{key}}",
    emptyTitle: "No inbound webhooks",
    emptyBody: "{{name}} doesn't send events to Sagot yet. Add an endpoint when the system can call a URL on changes.",
    lockTitle: "Inbound webhooks are included from the Pro plan",
    lockBody: "Let your systems push events to Sagot and start workflows the moment something changes. Upgrade to switch inbound webhooks on.",
    dialogHint: "How the sender proves who it is and how its payload becomes an event. The secret value is set under Secrets, in the slot you name.",
    key: "Endpoint key",
    keyHint: "Part of the address; it can't change later.",
    enabled: "Enabled",
    enabledAria: "Enable {{key}}",
    profile: "Verification",
    profiles: { standard_webhooks: "Standard Webhooks", generic_hmac: "Generic HMAC", shared_secret_header: "Shared secret header", basic: "HTTP Basic" },
    profileAbout: {
      standard_webhooks: "HMAC-SHA256 over id, timestamp and body, with a 300 second tolerance.",
      generic_hmac: "An HMAC over the body in a header you name.",
      shared_secret_header: "The sender repeats a shared secret in a header.",
      basic: "A user name and a password kept in the vault.",
    },
    secretSlot: "Secret slot",
    secretSlotHint: "Set its value under Secrets. The default is inbound_secret.",
    username: "User name",
    header: "Header",
    headerHint: "Leave empty for the default.",
    algorithm: "Algorithm",
    encoding: "Signature encoding",
    prefix: "Text before the signature",
    prefixHint: "Like sha256=",
    payload: "What is signed",
    timestampHeader: "Timestamp header",
    ipAllowlist: "Allowed source addresses (optional)",
    ipAllowlistHint: "Networks or addresses separated by spaces. Empty accepts any source.",
    eventFrom: "Event name from",
    eventFromBody: "A field of the body",
    eventFixed: "One fixed name",
    eventName: "Event name",
    eventNameHint: "Lowercase, like order.shipped.",
    eventPointer: "Field with the event name",
    eventPointerHint: "A pointer into the JSON body, like /type.",
    deliveryId: "Delivery id field (optional)",
    deliveryIdHint: "The sender's own id, used to drop duplicates for 7 days.",
    allow: "Expected events (optional)",
    allowHint: "Other events are kept for review but start nothing.",
    dataFields: "Data fields",
    dataFieldsShort: "Data for workflows",
    dataFieldsHint: "Each field becomes connector.data.<name> in the workflow that the event starts.",
    dataName: "Data field {{index}} name",
    dataSource: "Data field {{index}} source",
    dataRemove: "Remove data field {{index}}",
    addField: "Add data field",
    problems: {
      key: "Use lowercase letters, numbers, - and _, starting with a letter.",
      slot: "Use lowercase letters, numbers and underscores for the slot name.",
      required: "This field is required.",
      timestampHeader: "Name the header that carries the timestamp.",
      eventName: "Use lowercase letters, numbers, dots, - and _.",
      dataName: "Use lowercase letters, numbers and underscores.",
    },
    saved: "Endpoint saved",
    secretSet: "Secret set",
    secretMissing: "Set the secret \"{{slot}}\"",
    copyUrl: "Copy URL",
    lastReceived: "Last received",
    limits: "Up to 256 KB per body, signed and within 300 seconds, duplicates dropped for 7 days. Unsigned or late calls are refused with 401 and nothing is processed.",
    showDeliveries: "Recent deliveries",
    hideDeliveries: "Hide deliveries",
    deliveries: "Recent deliveries",
    noDeliveries: "Nothing received yet.",
    signature: "Signature",
    signatureOk: "Verified",
    signatureBad: "Failed",
    eventStatus: { received: "Received", processed: "Processed", unmatched: "Unmatched", rejected: "Rejected", failed: "Failed" },
    deleteAria: "Delete {{key}}",
    deleteTitle: "Delete the endpoint {{key}}?",
    deleteBody: "The sender will get an error from now on. Past deliveries are removed with it.",
    deleteConfirm: "Delete endpoint",
  },
};
